import {gql} from '@apollo/client';
import {
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import type {QueryExportDebtTransferLogsArgs, QueryGetDebtTransferLogsArgs} from '@igp/shared/contract/pam';
import type {DocumentNode} from 'graphql';

import type {
    DebtTransferLogDownloadFields,
    DebtTransferLogEntityType,
    DebtTransferLogFilterKeys,
    DebtTransferLogQueryFields,
    DebtTransferLogTextFilterKeys,
} from './types';

export interface IDebtTransferLogRequestBuilder
    extends GqlRequestBuilder<
            DebtTransferLogEntityType,
            QueryGetDebtTransferLogsArgs,
            DebtTransferLogQueryFields,
            DebtTransferLogFilterKeys
        >,
        IDownloadByLinkRequestBuilder<QueryExportDebtTransferLogsArgs, DebtTransferLogDownloadFields> {
    buildAdjustAgentDebtMutation(): DocumentNode;
}
export class DebtTransferLogRequestBuilder
    extends GqlRequestBuilder<
        DebtTransferLogEntityType,
        QueryGetDebtTransferLogsArgs,
        DebtTransferLogQueryFields,
        DebtTransferLogFilterKeys
    >
    implements IDebtTransferLogRequestBuilder
{
    buildAdjustAgentDebtMutation(): DocumentNode {
        return gql`
            mutation AdjustAgentDebt($adjustment_payload: AdjustAgentDebtInput!) {
                adjustAgentDebt(adjustment_payload: $adjustment_payload) {
                    log_id
                }
            }
        `;
    }
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<DebtTransferLogDownloadFields>
    ): GqlRequest<QueryExportDebtTransferLogsArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected validateFilters(_filter: Partial<Record<DebtTransferLogFilterKeys, unknown>>): void {
        return;
    }
    protected buildQuery(fields: DebtTransferLogQueryFields[]): DocumentNode {
        const requiredFields: DebtTransferLogQueryFields[] = ['log_id'];
        const queryFields = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetDebtTransferLogs($filter: DebtTransferLogsFilter, $start: Int, $end: Int, $sort: Sorting) {
                getDebtTransferLogs(filter: $filter, start: $start, end: $end, sort: $sort) {
                    items {
                        ${this.buildQueryFields(queryFields)}
                    }
                    total_count
                    last_updated {
                        seconds
                    }
                }
            }
        `;
    }
    protected buildFilter(filter: Partial<Record<DebtTransferLogFilterKeys, unknown>>): QueryGetDebtTransferLogsArgs {
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: DebtTransferLogTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                transfer_date: this.toGQLDateRange(filter?.['transfer_date.from'], filter?.['transfer_date.to']),
            },
        };
    }

    private readonly filterFieldsMapper: Record<DebtTransferLogTextFilterKeys, DebtTransferLogTextFilterKeys[]> = {
        agent_uid: ['agent_uid'],
        master_agent_uid: ['master_agent_uid'],
    };

    private buildExportQuery() {
        return gql`
            query ExportDebtTransferLogs(
                $filter: DebtTransferLogsFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportDebtTransferLogs(filter: $filter, report_name: $report_name, fields: $fields, sort: $sort, end: $end, start: $start) {
                    url
                }
            }
        `;
    }
}
